import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { useAppForm } from "@/lib/form/form-hook";
import { createLoginSchema } from "@/lib/form/schemas/auth";
import { useLogin } from "@/features/auth/hooks/useLogin";
import { usePasswordlessLogin, type PasswordlessLoginPhase } from "@/features/auth/hooks/useMfa";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { cancelCeremonies } from "@/lib/webauthn";
import { useCeremonyOwner } from "@/hooks/useCeremonyOwner";
import { Fingerprint } from "lucide-react";
import { useResendVerification } from "@/features/auth/hooks/useResendVerification";
import { Alert } from "@/components/ui/Alert";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { TurnstileChallenge } from "@/features/auth/components/TurnstileChallenge";
import { MfaChallenge } from "@/features/auth/components/MfaChallenge";
import {
  CHALLENGE_REQUIRED_CODE,
  TOO_MANY_VERIFICATION_EMAIL_REQUESTS_CODE,
  VERIFICATION_EMAIL_COOLDOWN_CODE,
  isEmailNotVerifiedError,
  isMfaRequired,
} from "@/features/auth/api/auth.api";
import { ApiError, getAccessKey } from "@/lib/api/client";
import { translateServerMessage } from "@/lib/api/server-messages";
import { isInternalPath, resolveLoginBouncePath } from "@/features/auth/post-login-redirect";
import {
  clearPersistedRecoveryCodesAtRisk,
  peekRecoveryCodesAtRisk,
} from "@/features/account/recovery-codes-at-risk";

const RESEND_RETRY_AFTER_MS = 60_000;

const EARLIER_LINK_CODES: ReadonlySet<string | undefined> = new Set([
  VERIFICATION_EMAIL_COOLDOWN_CODE,
  TOO_MANY_VERIFICATION_EMAIL_REQUESTS_CODE,
]);

const PASSKEY_BUTTON_LABEL = {
  idle: "auth.login.passkeySignIn",
  cancelable: "auth.login.passkeyCancel",
  verifying: "auth.login.submitting",
} as const satisfies Record<PasswordlessLoginPhase, string>;

const searchSchema = z.object({
  redirect: z
    .string()
    .optional()
    .transform((value) => (isInternalPath(value) ? value : undefined))
    .catch(undefined),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (!getAccessKey()) return;
    throw redirect({ to: resolveLoginBouncePath(search.redirect) });
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const loginMutation = useLogin();
  const ceremonyOwner = useCeremonyOwner();
  const passwordlessMutation = usePasswordlessLogin(ceremonyOwner);
  const supportsWebAuthn = browserSupportsWebAuthn();
  const resendMutation = useResendVerification();
  const schema = createLoginSchema(t);
  const [challengeRequired, setChallengeRequired] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const [resendReleasedAt, setResendReleasedAt] = useState(0);
  const [codesWarningCleared, setCodesWarningCleared] = useState(peekRecoveryCodesAtRisk);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const resendRef = useRef<HTMLButtonElement>(null);
  const resendOutcomeRef = useRef<HTMLParagraphElement>(null);
  const [mfaPasskeyStatusNode, setMfaPasskeyStatusNode] = useState<HTMLParagraphElement | null>(
    null,
  );
  const mfaPasskeyStatusRegion = (
    <p ref={setMfaPasskeyStatusNode} role="status" className="sr-only" />
  );

  const isUnverifiedError = isEmailNotVerifiedError(loginMutation.error);
  const unverifiedEmail = loginMutation.variables?.email.trim() ?? "";
  const resendError = resendMutation.error;
  const resendRateLimited = resendError?.status === 429;
  const resendCooldown = resendError?.code === VERIFICATION_EMAIL_COOLDOWN_CODE;
  const resendDelivered = resendMutation.isSuccess && resendMutation.data.emailSent !== false;
  const resendReleaseAt =
    resendMutation.isSuccess || resendCooldown
      ? resendMutation.submittedAt + RESEND_RETRY_AFTER_MS
      : 0;
  const resendWaiting = resendReleaseAt !== 0 && resendReleasedAt !== resendReleaseAt;
  const resendLocked =
    resendMutation.isPending || resendWaiting || (resendRateLimited && !resendCooldown);
  const resendSettledAt =
    resendMutation.isSuccess || resendMutation.isError ? resendMutation.submittedAt : 0;

  let resendOutcome = "";
  let resendOutcomeTone = "text-danger";
  if (resendDelivered) {
    resendOutcome = t("auth.login.resendSent", { email: resendMutation.variables });
    resendOutcomeTone = "text-success";
  } else if (resendMutation.isSuccess) {
    resendOutcome = `${t("auth.login.resendNotSent")} ${t("auth.login.resendEarlierLink")}`;
  } else if (resendError) {
    resendOutcome = translateServerMessage(resendError.rawError) ?? t("common.error");
    if (EARLIER_LINK_CODES.has(resendError.code)) {
      resendOutcome = `${resendOutcome} ${t("auth.login.resendEarlierLink")}`;
    }
  }

  useEffect(() => {
    if (codesWarningCleared) clearPersistedRecoveryCodesAtRisk();
  }, [codesWarningCleared]);

  useEffect(() => {
    if (!resendReleaseAt) return;
    const timer = window.setTimeout(
      () => setResendReleasedAt(resendReleaseAt),
      Math.max(0, resendReleaseAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [resendReleaseAt]);

  useEffect(() => {
    if (!resendSettledAt) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== resendRef.current) return;
    resendOutcomeRef.current?.focus();
  }, [resendSettledAt]);

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
    },
    validators: {
      onBlur: schema,
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      resendMutation.reset();
      try {
        await loginMutation.mutateAsync(challengeToken ? { ...value, challengeToken } : value);
      } catch (err) {
        if (err instanceof ApiError && err.code === CHALLENGE_REQUIRED_CODE) {
          setChallengeRequired(true);
        }
        setChallengeToken(null);
        setChallengeAttempt((attempt) => attempt + 1);
      }
    },
  });

  const mfaPending =
    loginMutation.data && isMfaRequired(loginMutation.data) ? loginMutation.data : null;
  if (mfaPending && codesWarningCleared) setCodesWarningCleared(false);

  const passkeyError = passwordlessMutation.error;
  const passkeyStopped = passkeyError !== null && !(passkeyError instanceof ApiError);
  const passkeyCanceled =
    passkeyStopped &&
    (passkeyError.name === "NotAllowedError" || passkeyError.name === "AbortError");

  useEffect(() => {
    if (!mfaPending) headingRef.current?.focus();
  }, [mfaPending]);

  if (mfaPending) {
    return (
      <AuthLayout>
        {mfaPasskeyStatusRegion}
        <MfaChallenge
          pending={mfaPending}
          passkeyStatusRegion={mfaPasskeyStatusNode}
          onRestart={() => {
            loginMutation.reset();
            setChallengeToken(null);
            setChallengeAttempt((attempt) => attempt + 1);
          }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {mfaPasskeyStatusRegion}
      <div className="mb-6 text-center">
        <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-bold text-heading outline-none">
          {t("auth.login.title")}
        </h1>
        <p className="mt-1 text-sm text-muted">{t("auth.login.subtitle")}</p>
      </div>

      {codesWarningCleared && (
        <Alert variant="warning" className="mb-4">
          {t("auth.login.codesWarningCleared")}
        </Alert>
      )}

      <ApiErrorBanner error={isUnverifiedError ? null : loginMutation.error} className="mb-4" />

      {isUnverifiedError && (
        <div className="mb-4">
          <Alert variant="danger">
            <p>{translateServerMessage(loginMutation.error?.rawError) ?? t("common.error")}</p>
            <p className="mt-1">{t("auth.login.resendHint")}</p>
          </Alert>
          <button
            ref={resendRef}
            type="button"
            onClick={() => {
              if (resendLocked || !unverifiedEmail) return;
              resendMutation.mutate(unverifiedEmail);
            }}
            aria-disabled={resendLocked || undefined}
            aria-busy={resendMutation.isPending || undefined}
            className="mt-2 text-xs font-medium text-indigo transition-colors hover:text-indigo-bright aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          >
            {resendMutation.isPending ? t("auth.login.resending") : t("auth.login.resend")}
          </button>
        </div>
      )}

      <p
        ref={resendOutcomeRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        tabIndex={-1}
        className={
          resendOutcome ? `mb-4 break-words text-xs outline-none ${resendOutcomeTone}` : "sr-only"
        }
      >
        {resendOutcome}
      </p>

      <form.AppForm>
        <form.Form className="flex flex-col gap-4" noValidate>
          <form.AppField
            name="email"
            children={(field) => (
              <field.TextField
                label={t("auth.login.email")}
                type="email"
                placeholder={t("auth.login.emailPlaceholder")}
                autoComplete="email"
              />
            )}
          />

          <form.AppField
            name="password"
            children={(field) => (
              <field.PasswordField
                label={t("auth.login.password")}
                placeholder={t("auth.login.passwordPlaceholder")}
                autoComplete="current-password"
              />
            )}
          />

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-indigo hover:text-indigo-bright transition-colors"
            >
              {t("auth.login.forgotPassword")}
            </Link>
          </div>

          {challengeRequired && (
            <TurnstileChallenge key={challengeAttempt} onToken={setChallengeToken} />
          )}

          <form.SubmitButton
            label={t("auth.login.submit")}
            loadingLabel={t("auth.login.submitting")}
          />
        </form.Form>
      </form.AppForm>

      {supportsWebAuthn && (
        <div className="mt-4">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted">{t("common.or")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <button
            type="button"
            onKeyDown={(event) => {
              if (event.repeat && event.key === "Enter") event.preventDefault();
            }}
            onClick={(event) => {
              if (event.detail > 1) return;
              if (passwordlessMutation.phase === "idle") passwordlessMutation.mutate();
              else if (passwordlessMutation.phase === "cancelable") cancelCeremonies(ceremonyOwner);
            }}
            aria-disabled={passwordlessMutation.phase === "verifying" || undefined}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-heading transition-colors hover:border-border-light aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          >
            <Fingerprint size={16} />
            {t(PASSKEY_BUTTON_LABEL[passwordlessMutation.phase])}
          </button>
          {passkeyError instanceof ApiError && (
            <p role="alert" className="mt-2 text-center text-xs text-danger">
              {passkeyError.error ?? t("common.error")}
            </p>
          )}
          {passkeyStopped && !passkeyCanceled && (
            <p role="alert" className="mt-2 text-center text-xs text-muted">
              {t("auth.login.passkeyFailed")}
            </p>
          )}
          <p
            role="status"
            className={passkeyCanceled ? "mt-2 text-center text-xs text-muted" : "sr-only"}
          >
            {passkeyCanceled ? t("auth.login.passkeyCanceled") : ""}
          </p>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-muted">
        {t("auth.login.noAccount")}{" "}
        <Link
          to="/register"
          className="font-medium text-indigo hover:text-indigo-bright transition-colors"
        >
          {t("auth.login.register")}
        </Link>
      </p>
    </AuthLayout>
  );
}
